// The iOS app (mobile/) is a native shell around this site. Capacitor puts
// its bridge into the page, so the site can use the app's native parts:
// Apple's own Sign in with Apple sheet and push notifications. On the web
// none of this runs, and the plugins' code is never even downloaded.
import { Capacitor } from '@capacitor/core'
import { api } from './api.js'

// The phone's push token, kept so signing out can stop its pushes.
const TOKEN_KEY = 'ph_push_token'
// Set once the player has answered (or put off) the "turn on notifications" card.
const ASKED_KEY = 'ph_push_asked'

export const isNativeApp = () => Capacitor.isNativePlatform()

function stored(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function store(key, value) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private mode or storage switched off: nothing to remember.
  }
}

export const pushToken = () => (isNativeApp() ? stored(TOKEN_KEY) : null)

// Apple's sheet, then the one-time code it hands back for the server
// (POST /auth/apple/native). Resolves to null when the player cancels.
export async function appleSignInCode() {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
  try {
    const { response } = await SignInWithApple.authorize({ clientId: 'us.predhub.app', redirectURI: `${location.origin}/auth/apple/callback`, scopes: '' })
    return response.authorizationCode
  } catch (err) {
    // Closing the sheet is "error 1001" from Apple.
    if (/1001|cancel/i.test(String(err?.message ?? err))) return null
    throw err
  }
}

// The module, not the plugin: a plugin is a proxy that answers every
// property, `then` included, so returning one from an async function (or
// awaiting it) treats it as a promise and fails.
const pushModule = () => import('@capacitor/push-notifications')

// 'granted', 'denied' or 'prompt' (not asked yet); null outside the app.
export async function pushPermission() {
  if (!isNativeApp()) return null
  const { PushNotifications } = await pushModule()
  const { receive } = await PushNotifications.checkPermissions()
  return receive.startsWith('prompt') ? 'prompt' : receive
}

export const pushAsked = () => stored(ASKED_KEY) === '1'
export const markPushAsked = () => store(ASKED_KEY, '1')

// Asks iOS for permission if it hasn't been asked yet, then registers. The
// token arrives in the listener set up by startNativeApp.
export async function enablePush() {
  const { PushNotifications: push } = await pushModule()
  let { receive } = await push.checkPermissions()
  if (receive.startsWith('prompt')) ({ receive } = await push.requestPermissions())
  markPushAsked()
  if (receive !== 'granted') return false
  await push.register()
  return true
}

// Turning pushes off for this phone, from settings.
export async function disablePush() {
  const token = stored(TOKEN_KEY)
  if (token) await api(`/api/me/push-devices/${encodeURIComponent(token)}`, { method: 'DELETE' }).catch(() => {})
  store(TOKEN_KEY, null)
  const { PushNotifications } = await pushModule()
  await PushNotifications.unregister()
}

// Once per app start: send the phone's token to whoever is signed in, and
// open the page a tapped notification points to. Returns a clean-up.
export async function startNativeApp({ navigate, signedIn }) {
  if (!isNativeApp()) return () => {}
  const { PushNotifications: push } = await pushModule()
  const handles = [
    await push.addListener('registration', ({ value }) => {
      store(TOKEN_KEY, value)
      api('/api/me/push-devices', { method: 'POST', body: { token: value } }).catch(() => {})
    }),
    await push.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const href = notification?.data?.href
      if (typeof href === 'string' && href.startsWith('/') && !href.startsWith('//')) navigate(href)
    }),
  ]
  // Tokens can change, so a signed-in phone with pushes on registers each start.
  if (signedIn && (await push.checkPermissions()).receive === 'granted') await push.register()
  return () => handles.forEach((h) => h.remove())
}
